import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import { Plus, CheckCircle, XCircle, Eye } from '@phosphor-icons/react';

type FilterTab = 'all' | 'pending' | 'approved' | 'rejected';

const REQUESTS = [
  {
    id: 'MR-0091',
    source: 'Chargebee',
    mapping: 'cf_referral_source -> marketing_channel',
    requestedBy: 'Darrel Brown',
    date: '2026-03-15',
    status: 'warning' as const,
    statusLabel: 'Pending',
  },
  {
    id: 'MR-0090',
    source: 'Recurly',
    mapping: 'custom_fields.tier -> subscription_tier',
    requestedBy: 'System (Auto)',
    date: '2026-03-14',
    status: 'success' as const,
    statusLabel: 'Approved',
  },
  {
    id: 'MR-0089',
    source: 'Braintree',
    mapping: 'customFields.loyalty_id -> loyalty_program_id',
    requestedBy: 'Darrel Brown',
    date: '2026-03-13',
    status: 'failed' as const,
    statusLabel: 'Rejected',
  },
];

function RequestsPage() {
  const [filter, setFilter] = useState<FilterTab>('all');

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: REQUESTS.length },
    { key: 'pending', label: 'Pending', count: 1 },
    { key: 'approved', label: 'Approved', count: 1 },
    { key: 'rejected', label: 'Rejected', count: 1 },
  ];

  const filtered = REQUESTS.filter((r) => {
    if (filter === 'pending') return r.status === 'warning';
    if (filter === 'approved') return r.status === 'success';
    if (filter === 'rejected') return r.status === 'failed';
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mapping Requests</h2>
          <p className="text-slate-500 text-sm mt-1">Review and approve field mapping requests from tenants and auto-detection.</p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition">
          <Plus size={18} weight="regular" />
          New Request
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition',
              filter === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-400 hover:text-slate-600',
            )}
          >
            {tab.label}
            <span className={cn(
              'ml-1.5 px-1.5 py-0.5 text-xs rounded-full',
              filter === tab.key ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400',
            )}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">Request ID</th>
              <th className="text-left px-6 py-3">Source</th>
              <th className="text-left px-6 py-3">Requested Mapping</th>
              <th className="text-left px-6 py-3">Requested By</th>
              <th className="text-left px-6 py-3">Date</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-center px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((req) => (
              <tr key={req.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition group">
                <td className="px-6 py-4 text-sm font-mono text-primary font-medium">{req.id}</td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                    {req.source}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-mono text-slate-600">{req.mapping}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{req.requestedBy}</td>
                <td className="px-6 py-4 text-sm text-slate-500">{req.date}</td>
                <td className="px-6 py-4">
                  <StatusBadge variant={req.status} label={req.statusLabel} />
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    {req.status === 'warning' && (
                      <>
                        <button className="text-green-500 hover:text-green-700 transition" title="Approve">
                          <CheckCircle size={20} weight="regular" />
                        </button>
                        <button className="text-rose-400 hover:text-rose-600 transition" title="Reject">
                          <XCircle size={20} weight="regular" />
                        </button>
                      </>
                    )}
                    <button className="text-slate-400 hover:text-slate-600 transition" title="View details">
                      <Eye size={20} weight="regular" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/requests')({
  component: RequestsPage,
});
